import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import PageShell from '@/components/PageShell';
import SectionHeader from '@/components/SectionHeader';
import { SOLUTIONS } from '@/lib/platforms';

const heroImg =
  'https://images.unsplash.com/photo-1611760357505-922600d8ffa6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxjYXIlMjBkYXNoYm9hcmQlMjBkYXJrfGVufDB8fHx8MTc4MTc5MjE2Nnww&ixlib=rb-4.1.0&q=85';

const Solutions = () => {
  return (
    <PageShell>
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt="instrument cluster"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/80 to-transparent" />
        </div>
        <div className="relative px-6 md:px-12 lg:px-16 pt-24 pb-16">
          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-px w-6 bg-amber inline-block" />
              Solutions
            </span>
          </div>
          <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl text-balance max-w-4xl leading-[1.02]">
            We ship what the industry actually runs on.
          </h1>
          <p className="mt-8 max-w-2xl text-zinc-300 text-base md:text-lg leading-relaxed">
            Solutions built on top of live, operating platforms — not slideware.
            For OEMs, dealer groups, insurers, lenders, and fleets.
          </p>
        </div>
      </section>

      {/* HOW WE PARTNER — positioning band */}
      <section className="relative px-6 md:px-12 lg:px-16 py-14 md:py-16 bg-ink-800 border-b border-white/10">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="overline text-amber mb-3">How we partner</div>
            <h2 className="font-display text-3xl md:text-4xl text-white leading-tight text-balance">
              Partners in the outcome — not a line item on a PO.
            </h2>
            <p className="mt-4 text-zinc-400 text-base leading-relaxed max-w-2xl">
              Every engagement is structured as a partnership — we bring
              operators, technology, AI &amp; data, and where it fits, capital.
              We co-own the outcome with you, on timelines and structures that
              fit your business.
            </p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
            {[
              ['Operators', 'In the room from day one'],
              ['Platforms', 'Production-grade, plug-in'],
              ['AI · Data', 'Sector-trained, not generic'],
              ['Capital', 'Outcome / equity / rev-share'],
            ].map(([k, v]) => (
              <div key={k} className="bg-ink-900 p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber mb-2">
                  {k}
                </div>
                <div className="text-zinc-300 text-sm leading-snug">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {SOLUTIONS.map((s, idx) => (
            <div key={s.audience} className="bg-ink-800 border border-white/10 p-8 md:p-10 hover:border-amber/40 transition-colors">
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">
                  / {String(idx + 1).padStart(2, '0')}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1">
                  For {s.audience}
                </span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl text-white mb-6">
                {s.title}
              </h3>
              <ul className="space-y-3">
                {s.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-3 text-zinc-300 text-sm md:text-base leading-relaxed">
                    <span className="text-amber font-mono mt-1 shrink-0">→</span>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/use-cases"
            className="group inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
          >
            See use cases &amp; impact
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-black border-y border-white/10">
        <SectionHeader
          kicker="How we engage"
          title="Three engagement models. One operating mindset."
          intro="Pick what fits — from a focused data product, to a full platform build, to a long-term partnership."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              t: 'Data & Insight Products',
              d: 'Subscription access to our market intelligence stack — market share, demand, EV, and dealer network data.',
            },
            {
              t: 'Custom Platform Build',
              d: 'White-label or co-branded platforms built on our DLIP, VehicleLedger.in, or AutoData.Exchange spine.',
            },
            {
              t: 'Strategic Partnership',
              d: 'Equity, revenue-share, or JV structures where we operate alongside you for the long term.',
            },
          ].map((b, i) => (
            <div key={b.t} className="bg-ink-900 border border-white/10 p-8 hover:border-amber/40 transition-colors">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan mb-4">
                Model 0{i + 1}
              </div>
              <h3 className="font-display text-2xl mb-3">{b.t}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-ink-900">
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl mb-6">
            Have a problem to solve?
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-8">
            Tell us what you're working on. If it's automotive or mobility,
            there's a strong chance we've already seen a version of it.
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
          >
            Brief us
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
};

export default Solutions;
